// src/components/Navigation.tsx
import * as React from "react";
import { Link } from "react-router-dom";
import { useSitePhone } from "@/hooks/useSitePhone";
import { serviceAreaNav } from "@/data/serviceAreaNav";

/* ----------------------------- DATA ----------------------------- */
export const serviceCategories = [
  {
    name: "Residential",
    slug: "residential",
    services: [
      { name: "Residential Electrician (All Home Services)", slug: "residential-electrician", isExternal: true },
      { name: "Electrical Repair & Troubleshooting", slug: "electrical-repair-troubleshooting" },
      { name: "Electrical Panel Upgrades", slug: "panel-upgrades" },
      { name: "Fuse Box to Breaker Conversions", slug: "fuse-box-conversions" },
      { name: "Pot Lights & LED Lighting", slug: "pot-lights-led-lighting" },
      { name: "Heated Driveways & Snow-Melt", slug: "heated-driveways" },
      { name: "Roof & Gutter De-Icing", slug: "roof-de-icing" },
      { name: "Electric Radiant Floor Heating", slug: "radiant-floor-heating" },
      { name: "Ceiling/Bathroom Fans, GFCI Outlets", slug: "ceiling-bathroom-fans-gfci" },
      { name: "Knob & Tube / Aluminum Wiring Repair", slug: "knob-tube-wiring" },
      { name: "Surge Protection & Smoke/CO Alarms", slug: "surge-protection-alarms" },
      { name: "Hot Tub, Pool & A/C Connections", slug: "hot-tub-pool-connections" },
      { name: "Security Camera Systems", slug: "camera-systems" },
      { name: "Motion Lighting & Security Lighting", slug: "motion-security-lighting" },
      { name: "Emergency 24/7", slug: "emergency", isExternal: true },
    ],
  },
  {
    name: "Commercial / Industrial",
    slug: "commercial",
    services: [
      { name: "Office, Retail & Restaurant Wiring", slug: "office-retail-restaurant" },
      { name: "Commercial Panel & Lighting Upgrades", slug: "panel-lighting-upgrades" },
      { name: "Transformers & Subpanels", slug: "transformers-subpanels" },
      { name: "Exit & Security Lighting", slug: "exit-security-lighting" },
      { name: "Power Distribution & Maintenance", slug: "power-distribution" },
      { name: "600V & 800A Electrical Services", slug: "600v-800a-services", category: "industrial" },
      { name: "Machinery Connections & Relocation", slug: "machinery-connections", category: "industrial" },
      { name: "Transformer & Motor Control Wiring", slug: "transformer-motor-control", category: "industrial" },
      { name: "PLC Wiring & Automation", slug: "plc-automation-wiring", category: "industrial" },
      { name: "Emergency 24/7", slug: "emergency", isExternal: true },
    ],
  },
  {
    name: "Lutron Home",
    slug: "smart-home",
    services: [
      { name: "Lutron HomeWorks", slug: "lutron-homeworks" },
      { name: "Lutron RadioRA 2", slug: "radiora-2" },
      { name: "Lutron RadioRA 3", slug: "radiora-3" },
      { name: "Lutron Palladiom Shades and Window Treatments", slug: "palladiom-shades" },
      { name: "Smart Panels & Occupancy Sensors", slug: "smart-panels-occupancy" },
      { name: "Voice Control Integration", slug: "voice-control" },
      { name: "Smart Thermostats & Security Systems", slug: "smart-thermostats" },
    ],
  },
  {
    name: "EV and Generator",
    slug: "ev-energy",
    services: [
      { name: "EV Charger Installation (Tesla, ChargePoint, etc.)", slug: "charger-installation" },
      { name: "Generator Wiring & Load Upgrades", slug: "generator-wiring" },
      { name: "Energy Management Systems", slug: "energy-management" },
    ],
  },
];

/* Areas We Serve lives in its own module so header + footer stay in sync */


const torontoNeighborhoods = [
  "Downtown Toronto","North York","Etobicoke","East York",
  "Midtown Toronto","The Beaches","Leslieville","High Park",
  "Junction Triangle","Forest Hill","Rosedale","Corktown",
  "Liberty Village","King West","Yorkville","Riverdale",
  "The Danforth","Bloor West","Little Italy","The Annex",
  "Kensington Market"
];

/* ------------------------- UI HELPERS -------------------------- */
export const triggerPill =
  "relative h-[44px] px-2 xl:px-3.5 inline-flex items-center gap-1.5 " +
  "bg-transparent border-0 " +
  "font-medium uppercase tracking-[0.1em] xl:tracking-[0.155em] whitespace-nowrap text-[10px] xl:text-[11px] leading-none snap-center " +
  "text-[#EADFC2] flex-shrink-0 transition-colors duration-300 ease-out " +
  "hover:text-[#F3D98A] " +
  "after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-[9px] after:h-[1.5px] after:w-0 " +
  "after:bg-gradient-to-r after:from-transparent after:via-[#D4AF37] after:to-transparent " +
  "after:transition-all after:duration-500 hover:after:w-[calc(100%-1.4rem)] " +
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent/50";

export const logoPill =
  "h-[40px] md:h-[42px] px-2 md:px-2.5 inline-flex items-center justify-center rounded-full " +
  "bg-transparent border border-transparent " +
  "flex-shrink-0 transition-all duration-300 ease-out " +
  "hover:bg-white/[0.04] hover:border-white/10 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60";

export const callButtonPill =
  "group h-[38px] px-4 inline-flex items-center gap-2 rounded-full " +
  "bg-accent text-accent-foreground border border-accent " +
  "font-semibold uppercase tracking-[0.12em] whitespace-nowrap text-[11.5px] leading-none snap-center " +
  "flex-shrink-0 transition-all duration-300 ease-out " +
  "hover:bg-accent-light hover:text-accent-foreground hover:border-accent-light " +
  "shadow-[0_4px_14px_-4px_hsl(var(--accent)/0.6)] " +
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent/60";

/* ----------------------------- MOBILE ----------------------------- */
const MobileAccordion = ({ label, children }: { label: string; children: React.ReactNode }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="w-full max-w-md">

      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`${triggerPill} w-full justify-center`}
        aria-expanded={open}
      >
        {label} <span aria-hidden className={`opacity-70 text-[1.05em] ml-1 transition-transform duration-200 ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && (
        <div className="mt-2 ml-2 pl-3 border-l-2 border-accent/30 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

const MobileNavLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} className={`${triggerPill} w-full justify-center`}>
    {children}
  </Link>
);

const MobileServiceLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link
    to={to}
    className="flex w-full items-center min-h-[44px] text-left px-4 py-2.5 rounded-lg text-[15px] font-semibold text-primary-foreground/90 hover:bg-white/[0.04] hover:text-accent transition-colors"
  >
    {children}
  </Link>
);

function MobileNavigation({ phoneLink }: { phoneLink: string }) {
  return (
    <nav className="w-full">
      <ul className="m-0 p-0 list-none flex flex-col items-center gap-2 w-full">
        <li className="w-full max-w-md">
          <Link
            to="/"
            className="block w-full text-center py-3 text-sm font-semibold tracking-wide text-primary-foreground/90 hover:text-accent transition-colors"
          >
            Home
          </Link>
        </li>


        <li className="w-full max-w-md">
          <MobileAccordion label="Panel Upgrades">
            <MobileServiceLink to="/residential/panel-upgrades">Electrical Panel Upgrades</MobileServiceLink>
            <MobileServiceLink to="/residential/fuse-box-conversions">Fuse Box to Breaker Conversions</MobileServiceLink>
            <MobileServiceLink to="/commercial/panel-lighting-upgrades">Commercial Panel & Lighting Upgrades</MobileServiceLink>
          </MobileAccordion>
        </li>

        <li className="w-full max-w-md">
          <MobileAccordion label="EV Charger">
            {serviceCategories[3].services
              .filter((s) => s.slug !== "generator-wiring")
              .map((s) => (
                <MobileServiceLink key={s.slug} to={`/${serviceCategories[3].slug}/${s.slug}`}>
                  {s.name}
                </MobileServiceLink>
              ))}
          </MobileAccordion>
        </li>

        <li className="w-full max-w-md">
          <MobileNavLink to="/ev-energy/generator-wiring">
            Generator
          </MobileNavLink>
        </li>

        <li className="w-full max-w-md">
          <MobileNavLink to="/residential/landscape-outdoor-lighting">
            Outdoor Lighting
          </MobileNavLink>
        </li>

        <li className="w-full max-w-md">
          <MobileAccordion label="Lutron">
            {serviceCategories[2].services.map((s) => (
              <MobileServiceLink key={s.slug} to={`/${serviceCategories[2].slug}/${s.slug}`}>
                {s.name}
              </MobileServiceLink>
            ))}
          </MobileAccordion>
        </li>

        <li className="w-full max-w-md">
          <MobileAccordion label="More Services">

            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider text-accent font-bold px-4 pt-1">Residential</p>
              {serviceCategories[0].services.map((s) => (
                <MobileServiceLink key={s.slug} to={s.isExternal ? `/${s.slug}` : `/residential/${s.slug}`}>
                  {s.name}
                </MobileServiceLink>
              ))}
            </div>
            <div className="space-y-2 mt-3 pt-3 border-t border-accent/20">
              <p className="text-xs uppercase tracking-wider text-accent font-bold px-4 pt-1">Commercial</p>
              <MobileServiceLink to="/commercial">All Commercial Electrical Services</MobileServiceLink>
              {serviceCategories[1].services.map((s) => (
                <MobileServiceLink key={s.slug} to={s.isExternal ? `/${s.slug}` : s.category === "industrial" ? `/industrial/${s.slug}` : `/commercial/${s.slug}`}>
                  {s.name}
                </MobileServiceLink>
              ))}
            </div>
          </MobileAccordion>
        </li>

        <li className="w-full max-w-md">
          <MobileAccordion label="Areas Served">
            {serviceAreaNav.map((area) => (
              <MobileServiceLink key={area.url} to={area.url}>
                {area.name}
              </MobileServiceLink>
            ))}
            <MobileServiceLink to="/gta">View all service areas</MobileServiceLink>
          </MobileAccordion>
        </li>


      </ul>
    </nav>
  );
}

/* ----------------------------- NAV ----------------------------- */
const DesktopNavigation = React.lazy(() => import("@/components/DesktopNavigation"));

function Navigation() {
  const { phoneLink } = useSitePhone();
  const [isDesktop, setIsDesktop] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <>
      {/* Desktop nav (with dropdown menus) loads only on large viewports */}
      {isDesktop && (
        <React.Suspense fallback={<div className="hidden lg:block h-[42px]" />}>
          <DesktopNavigation />
        </React.Suspense>
      )}

      {/* Mobile uses an accordion-style nav that stays within the viewport */}
      <div className="lg:hidden w-full">
        <MobileNavigation phoneLink={phoneLink} />
      </div>
    </>
  );
}

export { Navigation };
export default Navigation;
